// B2B Aging Report
import { useState, useMemo } from "react";
import { Timer, AlertCircle, Mail, Download, TrendingUp } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { ExportButton } from "@/components/admin/shared/ExportButton";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useAgingReport } from "@/hooks/admin/useB2B";
import type { AgingCustomerRow } from "@/lib/admin-api/b2b";

// ---------------------------------------------------------------------------
// Severity helpers
// ---------------------------------------------------------------------------

function getSeverity(
  oldestDays: number,
): "low" | "medium" | "high" | "critical" {
  if (oldestDays > 90) return "critical";
  if (oldestDays > 60) return "high";
  if (oldestDays > 30) return "medium";
  return "low";
}

const SEVERITY_ROW_COLORS: Record<string, string> = {
  low: "",
  medium: "",
  high: "bg-red-50/40",
  critical: "bg-red-50/70",
};

const SEVERITY_BADGE: Record<string, string> = {
  low: "bg-emerald-100 text-emerald-700",
  medium: "bg-amber-100 text-amber-700",
  high: "bg-red-100 text-red-700",
  critical: "bg-red-200 text-red-800",
};

const SEVERITY_LABELS: Record<string, string> = {
  low: "Faible",
  medium: "Moyen",
  high: "Eleve",
  critical: "Critique",
};

// ---------------------------------------------------------------------------
// Bucket bar visualization
// ---------------------------------------------------------------------------

function BucketBar({
  customerRow,
  totalOverdue,
}: {
  readonly customerRow: AgingCustomerRow;
  readonly totalOverdue: number;
}) {
  if (totalOverdue <= 0) return null;

  const pct = (v: number) => Math.max((v / totalOverdue) * 100, 0);

  const segments = [
    { value: customerRow.bucket_0_30, color: "bg-emerald-400", label: "0-30j" },
    {
      value: customerRow.bucket_31_60,
      color: "bg-amber-400",
      label: "31-60j",
    },
    { value: customerRow.bucket_61_90, color: "bg-red-400", label: "61-90j" },
    {
      value: customerRow.bucket_over_90,
      color: "bg-red-600",
      label: ">90j",
    },
  ].filter((s) => s.value > 0);

  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-stone-100">
      {segments.map((seg) => (
        <div
          key={seg.label}
          className={`${seg.color} transition-all`}
          style={{ width: `${pct(seg.value)}%` }}
          title={`${seg.label}: ${seg.value.toLocaleString("fr-SN")} FCFA`}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Relance (reminder) modal
// ---------------------------------------------------------------------------

interface RelanceModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly customer: AgingCustomerRow | null;
}

function RelanceModal({ open, onClose, customer }: RelanceModalProps) {
  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Envoyer une relance"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button className="gap-1.5" onClick={onClose}>
            <Mail className="h-4 w-4" />
            Envoyer
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-stone-600">
          Envoyer un email de relance a{" "}
          <span className="font-semibold">{customer?.company_name}</span> pour
          un solde en retard de{" "}
          <span className="font-semibold text-red-700">
            {customer?.amount_fcfa.toLocaleString("fr-SN")} FCFA
          </span>
          .
        </p>
        <p className="text-xs text-stone-400">
          La facture la plus ancienne date de{" "}
          {customer?.oldest_invoice_days} jours.
        </p>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Export columns
// ---------------------------------------------------------------------------

const EXPORT_COLUMNS = [
  { key: "company_name", label: "Client" },
  { key: "bucket_0_30", label: "0-30j (FCFA)" },
  { key: "bucket_31_60", label: "31-60j (FCFA)" },
  { key: "bucket_61_90", label: "61-90j (FCFA)" },
  { key: "bucket_over_90", label: ">90j (FCFA)" },
  { key: "amount_fcfa", label: "Total (FCFA)" },
  { key: "oldest_invoice_days", label: "Plus ancienne (jours)" },
  { key: "severity", label: "Severite" },
];

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Aging() {
  const [relanceCustomer, setRelanceCustomer] =
    useState<AgingCustomerRow | null>(null);
  const { data, isLoading, isError, refetch } = useAgingReport();

  const report = data;
  const customers = report?.customers ?? [];
  const buckets = report?.buckets ?? [];

  const bucketMap = useMemo(() => {
    const m: Record<string, { amount: number; count: number }> = {};
    for (const b of buckets) {
      m[b.label] = { amount: b.amount_fcfa, count: b.invoice_count };
    }
    return m;
  }, [buckets]);

  // Sort customers by severity (most critical first)
  const sortedCustomers = useMemo(
    () =>
      [...customers].sort(
        (a, b) => b.oldest_invoice_days - a.oldest_invoice_days,
      ),
    [customers],
  );

  // Summary stats
  const summaryStats = useMemo(() => {
    const totalOverdue = report?.total_overdue_fcfa ?? 0;
    const criticalCount = customers.filter(
      (c) => c.oldest_invoice_days > 90,
    ).length;
    const criticalAmount = customers
      .filter((c) => c.oldest_invoice_days > 90)
      .reduce((s, c) => s + c.bucket_over_90, 0);
    const avgDays =
      customers.length > 0
        ? Math.round(
            customers.reduce((s, c) => s + c.oldest_invoice_days, 0) /
              customers.length,
          )
        : 0;
    return { totalOverdue, criticalCount, criticalAmount, avgDays };
  }, [report, customers]);

  // Export data
  const exportData = useMemo(
    () =>
      sortedCustomers.map((c) => ({
        company_name: c.company_name,
        bucket_0_30: c.bucket_0_30,
        bucket_31_60: c.bucket_31_60,
        bucket_61_90: c.bucket_61_90,
        bucket_over_90: c.bucket_over_90,
        amount_fcfa: c.amount_fcfa,
        oldest_invoice_days: c.oldest_invoice_days,
        severity: SEVERITY_LABELS[getSeverity(c.oldest_invoice_days)],
      })) as Record<string, unknown>[],
    [sortedCustomers],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Timer className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Balance agee
              </h1>
              <p className="text-sm text-stone-500">
                Suivi des creances clients en retard
              </p>
            </div>
          </div>
          <ExportButton
            data={exportData}
            columns={EXPORT_COLUMNS}
            filename="balance-agee"
          />
        </div>

        {/* Error */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger la balance agee.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Reessayer
            </Button>
          </div>
        )}

        {!isError && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              <KPICard
                label="Total en retard"
                value={summaryStats.totalOverdue}
                format="currency"
                icon={<Timer className="h-4 w-4" />}
              />
              {(["0-30j", "31-60j", "61-90j", ">90j"] as const).map(
                (label) => (
                  <KPICard
                    key={label}
                    label={label}
                    value={bucketMap[label]?.amount ?? 0}
                    format="currency"
                  />
                ),
              )}
              <KPICard
                label="Retard moyen"
                value={`${summaryStats.avgDays}j`}
                icon={<TrendingUp className="h-4 w-4" />}
              />
            </div>

            {/* Critical alert */}
            {summaryStats.criticalCount > 0 && (
              <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>
                  <strong>{summaryStats.criticalCount}</strong> client(s) avec
                  des creances de plus de 90 jours pour un total de{" "}
                  <strong>
                    {summaryStats.criticalAmount.toLocaleString("fr-SN")} FCFA
                  </strong>
                  . Action immediate recommandee.
                </span>
              </div>
            )}

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-stone-500">
              <span className="font-medium">Legende :</span>
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                0-30j
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                31-60j
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                61-90j
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-red-600" />
                &gt;90j
              </span>
            </div>

            {/* Customer aging table */}
            {!isLoading && sortedCustomers.length === 0 ? (
              <EmptyState
                icon={<Timer className="h-6 w-6" />}
                title="Aucune creance en retard"
                description="Tous les paiements sont a jour."
              />
            ) : (
              <div className="overflow-x-auto rounded-lg border border-stone-200 bg-white">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-100 bg-stone-50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-stone-500">
                        Client
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-stone-500">
                        Repartition
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-stone-500">
                        0-30j
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-stone-500">
                        31-60j
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-red-500">
                        61-90j
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-red-600">
                        &gt;90j
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-stone-500">
                        Total
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-stone-500">
                        Severite
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-stone-500">
                        Plus ancienne
                      </th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading
                      ? Array.from({ length: 4 }).map((_, i) => (
                          <tr
                            key={i}
                            className="border-b border-stone-50 animate-pulse"
                          >
                            {Array.from({ length: 10 }).map((__, j) => (
                              <td key={j} className="px-4 py-3">
                                <div className="h-4 rounded bg-stone-100" />
                              </td>
                            ))}
                          </tr>
                        ))
                      : sortedCustomers.map((c) => {
                          const severity = getSeverity(c.oldest_invoice_days);
                          return (
                            <tr
                              key={c.customer_id}
                              className={`border-b border-stone-50 hover:bg-stone-50/50 ${SEVERITY_ROW_COLORS[severity]}`}
                            >
                              <td className="px-4 py-3 font-medium text-stone-900">
                                {c.company_name}
                              </td>
                              <td className="px-4 py-3 min-w-[120px]">
                                <BucketBar
                                  customerRow={c}
                                  totalOverdue={c.amount_fcfa}
                                />
                              </td>
                              <td className="px-4 py-3 text-right text-stone-600">
                                {c.bucket_0_30 > 0
                                  ? `${c.bucket_0_30.toLocaleString("fr-SN")}`
                                  : "---"}
                              </td>
                              <td className="px-4 py-3 text-right text-stone-600">
                                {c.bucket_31_60 > 0
                                  ? `${c.bucket_31_60.toLocaleString("fr-SN")}`
                                  : "---"}
                              </td>
                              <td
                                className={`px-4 py-3 text-right ${
                                  c.bucket_61_90 > 0
                                    ? "font-medium text-red-600"
                                    : "text-stone-400"
                                }`}
                              >
                                {c.bucket_61_90 > 0
                                  ? `${c.bucket_61_90.toLocaleString("fr-SN")}`
                                  : "---"}
                              </td>
                              <td
                                className={`px-4 py-3 text-right ${
                                  c.bucket_over_90 > 0
                                    ? "font-medium text-red-700"
                                    : "text-stone-400"
                                }`}
                              >
                                {c.bucket_over_90 > 0
                                  ? `${c.bucket_over_90.toLocaleString("fr-SN")}`
                                  : "---"}
                              </td>
                              <td className="px-4 py-3 text-right font-semibold text-stone-900">
                                {c.amount_fcfa.toLocaleString("fr-SN")} FCFA
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span
                                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${SEVERITY_BADGE[severity]}`}
                                >
                                  {SEVERITY_LABELS[severity]}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right text-stone-500">
                                {c.oldest_invoice_days}j
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  type="button"
                                  onClick={() => setRelanceCustomer(c)}
                                  className="flex items-center gap-1 rounded-md bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100"
                                >
                                  <Mail className="h-3 w-3" />
                                  Relance
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <RelanceModal
        open={relanceCustomer !== null}
        onClose={() => setRelanceCustomer(null)}
        customer={relanceCustomer}
      />
    </AdminLayout>
  );
}
